import { createSlice } from "@reduxjs/toolkit";
import { sortAllView } from "../utils";
import { boardViewForAllocation } from "./TransferOrderSliceV2";

/**
 * Mirror of ReceiptHeaderQuerySet.annotate_statuses overall_status.
 * Soft-deleted lines are ignored. Mixed approved + cancelled is Partially Approved.
 */
const getReceiptOverallStatus = (items = []) => {
  const activeItems = items.filter(item => !item.is_deleted);
  const total = activeItems.length;
  if (total === 0) return "Created";

  let approved = 0;
  let cancelled = 0;
  for (const item of activeItems) {
    if (item.is_cancelled) cancelled += 1;
    else if (item.is_receipted) approved += 1;
  }

  if (cancelled === total) return "Cancelled";
  if (approved === total) return "Approved";
  if (approved > 0) return "Partially Approved";
  return "Created";
};

const receiptAdjustmentDefaultColumns = [
  "RECEIPT ADJUSTMENT NO",
  "RECEIPT NO",
  "DOCUMENT NO",
  "DOCUMENT TYPE",
  "POSTING DATE",
  "SHIPMENT NO",
  "CREATED BY",
  "STATUS",
];

const initialState = {
  receipt: null,
  receipts: { count: 0, results: [] },
  receiptsParams: { limit: 10, offset: 0 },
  receiptsSelection: [],

  receiptDateRangeSelection: { startDate: "", endDate: "", reset: false, key: "" },

  receiptAdjustment: null,
  receiptAdjustments: { count: 0, results: [] },
  receiptAdjustmentsParams: { limit: 10, offset: 0 },
  receiptAdjustmentSelection: [],

  receiptsLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: [
        "RECEIPT NO",
        "DOCUMENT NUMBER",
        "DOCUMENT TYPE",
        "SHIPMENT NUMBER",
        "SHIPMENT",
        "TO FACILITY",
        "POSTING DATE",
        "CREATED BY",
        "STATUS",
      ],
      allViews: {
        "Default View": [
          "RECEIPT NO",
          "DOCUMENT NUMBER",
          "DOCUMENT TYPE",
          "SHIPMENT NUMBER",
          "SHIPMENT",
          "TO FACILITY",
          "POSTING DATE",
          "CREATED BY",
          "STATUS",
        ],
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },
  receiptAdjustmentLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: receiptAdjustmentDefaultColumns,
      allViews: {
        "Default View": receiptAdjustmentDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },
};

const receiptSlice = createSlice({
  name: "receipt",
  initialState,
  reducers: {
    resetReceipt: state => {
      state.receipt = initialState.receipt;
    },
    resetReceiptAdjustment: state => {
      state.receiptAdjustment = initialState.receiptAdjustment;
    },
    resetReceipts: state => {
      state.receipts = initialState.receipts;
    },
    resetReceiptsParams: state => {
      state.receiptsParams = { limit: state.receiptsParams.limit, offset: 0 };
    },
    resetReceiptsSelection: state => {
      state.receiptsSelection = initialState.receiptsSelection;
    },
    resetReceiptAdjustmentsParams: state => {
      state.receiptAdjustmentsParams = { limit: state.receiptAdjustmentsParams.limit, offset: 0 };
    },
    setReceiptDateSelectionRange: (state, action) => {
      state.receiptDateRangeSelection = action.payload;
    },
    setReceipt: (state, action) => {
      state.receipt = action.payload;
    },
    setReceipts: (state, action) => {
      const { count, results } = action.payload;
      state.receipts = { count, results };
    },
    setReceiptsLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.receiptsLayout = {
        ...state.receiptsLayout,
        listView: {
          ...state.receiptsLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.receiptsLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setReceiptsParams: (state, action) => {
      state.receiptsParams = action.payload;
    },
    setReceiptsSelection: (state, action) => {
      state.receiptsSelection = action.payload;
    },
    setReceiptAdjustment: (state, action) => {
      state.receiptAdjustment = action.payload;
    },
    setReceiptAdjustments: (state, action) => {
      state.receiptAdjustments = action.payload;
    },
    setReceiptAdjustmentSelection: (state, action) => {
      state.receiptAdjustmentSelection = action.payload;
    },
    setReceiptAdjustmentLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.receiptAdjustmentLayout = {
        ...state.receiptAdjustmentLayout,
        listView: {
          ...state.receiptAdjustmentLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.receiptAdjustmentLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    updateReceiptsParams: (state, action) => {
      state.receiptsParams = { ...state.receiptsParams, ...action.payload };
    },
    updateReceiptAdjustmentsParams: (state, action) => {
      state.receiptAdjustmentsParams = { ...state.receiptAdjustmentsParams, ...action.payload };
    },
    updateReceipt: (state, action) => {
      const { receipt_line_ids = [], status } = action.payload;

      // Guard: must have a loaded receipt and at least one id.
      if (!state.receipt || !Array.isArray(receipt_line_ids) || receipt_line_ids.length === 0) {
        return;
      }

      const ids = new Set(receipt_line_ids);

      // Assume state.receipt.items exists; guard if not
      const items = state.receipt.items;
      if (!Array.isArray(items)) return;

      items.forEach(item => {
        if (!ids.has(item.id)) return;

        if (status === "approved") {
          item.is_receipted = true;
        } else if (status === "cancelled") {
          item.is_cancelled = true;
        }
      });

      state.receipt.overall_status = getReceiptOverallStatus(items);
    },
  },
});

export const {
  resetReceipt,
  resetReceiptAdjustment,
  resetReceipts,
  resetReceiptsParams,
  resetReceiptsSelection,
  resetReceiptAdjustmentsParams,
  setReceiptDateSelectionRange,
  setReceipt,
  setReceipts,
  setReceiptsLayoutAllViews,
  setReceiptsParams,
  setReceiptsSelection,
  setReceiptAdjustment,
  setReceiptAdjustments,
  setReceiptAdjustmentSelection,
  setReceiptAdjustmentLayoutAllViews,
  updateReceiptsParams,
  updateReceiptAdjustmentsParams,
  updateReceipt,
} = receiptSlice.actions;

export default receiptSlice.reducer;
